import * as React from "react";
import { Breakpoint } from "@material-ui/core/styles/createBreakpoints";
import { entries, fill, flatten, groupBy, zipWith } from "lodash";
import { Moment } from "moment";
import {
  Area,
  AreaChart,
  AxisDomain,
  Bar,
  BarChart,
  ComposedChart,
  Legend,
  Line,
  PositionType,
  ReferenceLine,
  ResponsiveContainer,
  ScaleType,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatBigNumber, formatMoney } from "lib/helpers";
import { EarningByRegion } from "services/valuation/types";
import theme, { useWidth } from "theme";

import { CustomTooltip } from "./CustomTooltip";

const getPeriodLabel = (date: Moment, distributionFrequency: number) => {
  if (distributionFrequency === 1) {
    return "1Y";
  } else if (distributionFrequency === 2) {
    return date.quarter() < 3 ? "1H" : "2H";
  } else if (distributionFrequency === 4) {
    return `${date.quarter()}Q`;
  } else {
    return date.format("MMM");
  }
};

const useMinimumPeriods = (distributionFrequency: number) => {
  const width = useWidth();
  if (distributionFrequency === 1) {
    return 1;
  } else if (distributionFrequency === 2) {
    const minimumPeriodsByWidth: Record<Breakpoint, number> = {
      xs: 2,
      sm: 1,
      md: 1,
      lg: 1,
      xl: 1,
    };
    return minimumPeriodsByWidth[width];
  } else if (distributionFrequency === 4) {
    const minimumPeriodsByWidth: Record<Breakpoint, number> = {
      xs: 2,
      sm: 2,
      md: 1,
      lg: 1,
      xl: 1,
    };
    return minimumPeriodsByWidth[width];
  } else {
    // distributionFrequency === 12
    const minimumPeriodsByWidth: Record<Breakpoint, number> = {
      xs: 8,
      sm: 5,
      md: 3,
      lg: 2,
      xl: 2,
    };
    return minimumPeriodsByWidth[width];
  }
};
interface ChartEntry {
  date: Moment;
  amount: number | EarningByRegion;
}

export type ChartType = "area-chart" | "bar-chart" | "stacked-bar-chart";

interface RecentEarningsProps {
  data: ChartEntry[];
  chartType?: ChartType;
  padding: number;
  height: number;
  marginVertical: number;
  marginHorizontal: number;
  strokeColor?: string;
  fillColor?: string;
  shouldHideXLabel?: boolean;
  secondXAxisCustomTicks?: React.ReactNode;
  xLabelPosition?: PositionType;
  customYAxisDomain?: [AxisDomain, AxisDomain];
  customYAxisScale?: ScaleType;
  yLabelOffset?: number;
  distributionFrequency: number;
  showUnreportedRegion?: boolean;
}

const RecentEarnings: React.FC<RecentEarningsProps> = ({
  data,
  chartType,
  padding,
  height,
  marginVertical,
  marginHorizontal,
  strokeColor,
  fillColor,
  secondXAxisCustomTicks,
  customYAxisDomain,
  customYAxisScale,
  distributionFrequency,
  showUnreportedRegion,
}) => {
  const firstYear = String(Math.min(...data.map(x => x.date.year())));
  const minimumPeriodsToShowFirstYear = useMinimumPeriods(
    distributionFrequency,
  );

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = React.useState(0);
  React.useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (entry?.contentRect?.width) {
        setChartWidth(entry.contentRect.width);
      }
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);
  const parsedData = flatten(
    entries(groupBy(data, (x: ChartEntry) => x.date.year()))
      .map(([year, entriesArray]) => {
        return {
          // don't display the year if we have less than the minimum number of periods required to show the first year without overlapping text
          yearAxis:
            year === firstYear &&
            entriesArray.length < minimumPeriodsToShowFirstYear
              ? fill(Array(entriesArray.length), "")
              : [String(year), ...fill(Array(entriesArray.length - 1), "")],
          entries: entriesArray,
        };
      })
      .map(({ entries, yearAxis }) =>
        zipWith(entries, yearAxis, (entry, yearLabel) => {
          const dataItem = {
            x: `${getPeriodLabel(
              entry.date,
              distributionFrequency,
            )} ${entry.date.year()}`,
            x1: getPeriodLabel(entry.date, distributionFrequency),
            x2: yearLabel,
          };

          if (typeof entry.amount === "number") {
            return {
              ...dataItem,
              y: parseFloat(formatMoney(entry.amount, 0, ".", "")),
            };
          } else {
            return {
              ...dataItem,
              domestic: parseFloat(
                formatMoney(entry.amount.domestic, 0, ".", ""),
              ),
              international: parseFloat(
                formatMoney(entry.amount.intl, 0, ".", ""),
              ),
              unreported: parseFloat(
                formatMoney(entry.amount.unreported, 0, ".", ""),
              ),
              net: parseFloat(formatMoney(entry.amount.total, 0, ".", "")),
              y: parseFloat(formatMoney(entry.amount.total, 0, ".", "")),
            };
          }
        }),
      ),
  );

  const CustomLegend = ({
    payload,
  }: {
    payload: Array<{ value: string; color: string }>;
  }) => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "1.5em",
          flexWrap: "wrap",
        }}
      >
        {payload.map((entry: { value: string; color: string }) => {
          const { value, color } = entry;
          return (
            <span
              key={value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {value === "net" ? (
                <>
                  <svg width="16" height="16">
                    <line
                      x1="2"
                      x2="14"
                      y1="8"
                      y2="8"
                      stroke="#000"
                      strokeWidth="2"
                    />
                  </svg>
                  Net
                </>
              ) : (
                <>
                  <svg width="12" height="12">
                    <rect width="12" height="12" fill={color} />
                  </svg>
                  {value.charAt(0).toUpperCase() + value.slice(1)}
                </>
              )}
            </span>
          );
        })}
      </div>
    );
  };

  const chartWrapperMap = new Map<ChartType, any>([
    ["area-chart", AreaChart],
    ["bar-chart", BarChart],
    ["stacked-bar-chart", ComposedChart],
  ]);

  const totalLineLength = Math.max(2, (chartWidth / parsedData.length) * 0.25);
  const makeChartMap = (showUnreportedRegion: boolean) => {
    const chartMap = new Map<ChartType, React.ReactNode[]>([
      [
        "area-chart",
        [
          <Area
            key="area-chart"
            type="linear"
            dataKey="y"
            fill={fillColor}
            stroke={strokeColor || theme.custom.palette.red.main}
            strokeWidth={3}
            isAnimationActive={false}
          />,
        ],
      ],
      [
        "bar-chart",
        [
          <Bar
            key="bar-chart"
            layout="horizontal"
            dataKey="y"
            fill={fillColor}
            isAnimationActive={false}
          />,
        ],
      ],
      [
        "stacked-bar-chart",
        [
          <Tooltip
            key="stacked-bar-tooltip"
            content={<CustomTooltip displayTotal={false} />}
          />,
          <Bar
            key="domestic-bar-chart"
            layout="horizontal"
            dataKey="domestic"
            fillOpacity={0.8}
            fill={theme.custom.palette.purple.main}
            stackId="earnings"
            isAnimationActive={false}
          />,
          <Bar
            key="international-bar-chart"
            layout="horizontal"
            dataKey="international"
            fillOpacity={0.8}
            fill={theme.custom.palette.green.main}
            stackId="earnings"
            isAnimationActive={false}
          />,
          <Line
            key="net-line"
            dataKey="net"
            stroke="#000"
            strokeWidth={0}
            dot={({ cx, cy }) => (
              <line
                x1={cx - totalLineLength}
                x2={cx + totalLineLength}
                y1={cy}
                y2={cy}
                stroke={theme.custom.palette.black.main}
                strokeWidth={2}
              />
            )}
            isAnimationActive={false}
          />,
          <Legend
            key="stacked-bar-legend"
            content={props => {
              const payload = (props.payload as unknown) as Array<{
                value: string;
                color: string;
              }>;
              return CustomLegend({ payload });
            }}
          />,
        ],
      ],
    ]);
    if (showUnreportedRegion) {
      chartMap
        .get("stacked-bar-chart")
        .splice(
          3,
          0,
          <Bar
            key="unreported-bar-chart"
            layout="horizontal"
            dataKey="unreported"
            fillOpacity={0.8}
            fill={theme.custom.palette.gray.dark}
            stackId="earnings"
            isAnimationActive={false}
          />,
        );
    }
    return chartMap;
  };

  const chartMap = makeChartMap(showUnreportedRegion);

  const ChartWrapper =
    chartWrapperMap.get(chartType) || chartWrapperMap.get("area-chart");
  const chartElement = chartMap.get(chartType) || chartMap.get("area-chart");

  return (
    <div ref={containerRef} style={{ width: "100%", height: height }}>
      <ResponsiveContainer
        width="99%"
        height={height}
        className="chart-container"
      >
        <ChartWrapper
          data={parsedData}
          margin={{
            top: marginVertical,
            right: marginHorizontal + 5, // add some right margin for the year axis: year is clipped on monthly earnings when only first month of the year is available
            left: marginHorizontal,
            bottom: marginVertical,
          }}
          stackOffset="sign"
        >
          <XAxis
            dataKey="x1"
            stroke={theme.custom.palette.black.main}
            xAxisId={0}
            tickLine={false}
            padding={{ left: padding, right: padding }}
            tickFormatter={value => (value as string).split(" ")[0]}
          />
          <XAxis
            dataKey="x2"
            xAxisId={1}
            stroke="1"
            padding={{ left: padding, right: padding }}
            tick={secondXAxisCustomTicks as React.ReactElement}
            interval={0}
          />
          <YAxis
            tickLine={false}
            stroke={theme.custom.palette.black.main}
            tickFormatter={value => `$${formatBigNumber(value)}`}
            domain={customYAxisDomain || [0, "auto"]}
            scale={customYAxisScale || "auto"}
          />
          <ReferenceLine
            y={0}
            stroke={theme.custom.palette.black.main}
            strokeWidth={1}
            strokeDasharray="4 2"
          />
          {chartType !== "stacked-bar-chart" && (
            <Tooltip
              formatter={value => [
                `$${formatMoney(value as number, 0)}`,
                "amount",
              ]}
            />
          )}
          {chartElement}
        </ChartWrapper>
      </ResponsiveContainer>
    </div>
  );
};

export default RecentEarnings;
